/**
 * HTTP Circuit Breaker - Protects external HTTP service calls from cascading failures.
 *
 * STATE MACHINE:
 *   closed  → open      (after failureThreshold consecutive failures)
 *   open    → half_open (after resetWindowMs elapses)
 *   half_open → closed  (after halfOpenMaxCalls successful probes)
 *   half_open → open    (if probe fails)
 *
 * DESIGN:
 *   Mirrors the Redis circuit breaker in redisManager.ts exactly.
 *   If the Redis protection standard changes, this must change to match.
 *
 * GOVERNANCE RULE (Canonical Ownership Doctrine §IX + Subsystem 6):
 *   Open circuit = service unavailable.
 *   Callers MUST NOT report the guarded service as "healthy" when circuit is open.
 *   Fallback values are permitted - fabricated success responses are not.
 *
 * SINGLETON INSTANCES (bottom of file):
 *   supabaseCircuit  - protects all Supabase read/write calls
 *   anthropicCircuit - protects Anthropic API calls
 *   openaiCircuit    - protects OpenAI API calls
 */

import { eventBus } from "./eventBus";
import { logger }   from "@/system/logger";

/* =========================================================
   TYPES
   ========================================================= */

export type CircuitState = "closed" | "open" | "half_open";

export interface CircuitBreakerConfig {
  /** Unique identifier for this circuit - used in events and logs. */
  serviceId:        string;
  /** Number of consecutive failures before the circuit opens. */
  failureThreshold: number;
  /** Milliseconds to wait in "open" state before probing with half-open. */
  resetWindowMs:    number;
  /** Per-call timeout in milliseconds (before counting as a failure). */
  timeoutMs:        number;
  /** Number of successful half-open probes needed before closing. */
  halfOpenMaxCalls: number;
  /** Random jitter factor ±percentage applied to timeoutMs (default 0.2 = ±20%). */
  jitterFactor:     number;
}

export interface CircuitHealthSnapshot {
  serviceId:       string;
  state:           CircuitState;
  failureCount:    number;
  lastFailedAt:    number | null;
  lastSucceededAt: number | null;
  totalRejected:   number;
  openedAt:        number | null;
}

/* =========================================================
   ERRORS
   ========================================================= */

export class CircuitOpenError extends Error {
  readonly serviceId: string;

  constructor(serviceId: string) {
    super(`Circuit open - ${serviceId} is currently unavailable`);
    this.name      = "CircuitOpenError";
    this.serviceId = serviceId;
  }
}

export class CircuitTimeoutError extends Error {
  readonly serviceId: string;
  readonly timeoutMs: number;

  constructor(serviceId: string, timeoutMs: number) {
    super(`Circuit timeout - ${serviceId} did not respond within ${timeoutMs}ms`);
    this.name      = "CircuitTimeoutError";
    this.serviceId = serviceId;
    this.timeoutMs = timeoutMs;
  }
}

/* =========================================================
   HTTP CIRCUIT BREAKER CLASS
   ========================================================= */

export class HttpCircuitBreaker {
  private state:           CircuitState  = "closed";
  private failureCount:    number        = 0;
  private lastFailedAt:    number | null = null;
  private lastSucceededAt: number | null = null;
  private totalRejected:   number        = 0;
  private openedAt:        number | null = null;
  private halfOpenCalls:   number        = 0;

  constructor(private readonly config: CircuitBreakerConfig) {}

  /* ── PUBLIC EXECUTE ─────────────────────────────────── */

  /**
   * Execute a function through the circuit breaker.
   *
   * @param fn       The async operation to protect.
   * @param options  Optional fallback and AbortSignal.
   *
   * @throws CircuitOpenError  if circuit is open and no fallback is provided.
   * @throws CircuitTimeoutError if the operation exceeds the configured timeout.
   * @throws whatever fn() throws (after recording as a failure).
   *
   * GOVERNANCE: If a fallback is provided, it is used when the circuit is open.
   *             Fallback values must be honest (e.g., { found: false }) - not
   *             fabricated success responses.
   */
  async execute<T>(
    fn: () => Promise<T>,
    options?: {
      fallback?: () => T;
      signal?:   AbortSignal;
    },
  ): Promise<T> {
    // ── Gate: check circuit state ──────────────────────
    if (this.state === "open") {
      const elapsed = Date.now() - (this.lastFailedAt ?? 0);
      if (elapsed >= this.config.resetWindowMs) {
        this.transitionTo("half_open");
      } else {
        this.totalRejected++;
        eventBus.emit({
          type:      "circuit:rejected",
          serviceId: this.config.serviceId,
          reason:    "circuit_open",
        });
        if (options?.fallback) return options.fallback();
        throw new CircuitOpenError(this.config.serviceId);
      }
    }

    // ── Execute with timeout + jitter ──────────────────
    const jitter    = 1 + (Math.random() * 2 - 1) * this.config.jitterFactor;
    const timeoutMs = Math.round(this.config.timeoutMs * jitter);

    let timeoutHandle: ReturnType<typeof setTimeout> | null = null;

    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutHandle = setTimeout(
        () => reject(new CircuitTimeoutError(this.config.serviceId, timeoutMs)),
        timeoutMs,
      );
      // Respect external AbortSignal
      options?.signal?.addEventListener("abort", () => {
        if (timeoutHandle !== null) clearTimeout(timeoutHandle);
        reject(new Error(`Aborted: ${this.config.serviceId}`));
      });
    });

    try {
      const result = await Promise.race([fn(), timeoutPromise]);
      if (timeoutHandle !== null) clearTimeout(timeoutHandle);
      this.onSuccess();
      return result;
    } catch (err) {
      if (timeoutHandle !== null) clearTimeout(timeoutHandle);
      this.onFailure(err);
      throw err;
    }
  }

  /* ── STATE MACHINE ──────────────────────────────────── */

  private onSuccess(): void {
    this.lastSucceededAt = Date.now();

    if (this.state === "half_open") {
      this.halfOpenCalls++;
      if (this.halfOpenCalls >= this.config.halfOpenMaxCalls) {
        this.transitionTo("closed");
      }
    } else {
      // Reset failure streak on success while closed
      this.failureCount = 0;
    }
  }

  private onFailure(err: unknown): void {
    this.lastFailedAt = Date.now();
    this.failureCount++;

    const message = err instanceof Error ? err.message : String(err);
    logger.warn(`HttpCircuitBreaker[${this.config.serviceId}]: failure`, {
      failureCount: this.failureCount,
      threshold:    this.config.failureThreshold,
      state:        this.state,
      error:        message,
    });

    if (this.state === "half_open") {
      // Probe failure - circuit reopens immediately
      this.transitionTo("open");
    } else if (this.failureCount >= this.config.failureThreshold) {
      this.transitionTo("open");
    }
  }

  private transitionTo(newState: CircuitState): void {
    const previous = this.state;
    this.state = newState;

    if (newState === "open") {
      this.openedAt = Date.now();
      const resetAt = this.openedAt + this.config.resetWindowMs;
      eventBus.emit({
        type:         "circuit:opened",
        serviceId:    this.config.serviceId,
        failureCount: this.failureCount,
        resetAt,
      });
      logger.warn(`HttpCircuitBreaker[${this.config.serviceId}]: OPENED`, {
        failureCount: this.failureCount,
        resetAt:      new Date(resetAt).toISOString(),
        previous,
      });

    } else if (newState === "half_open") {
      this.halfOpenCalls = 0;
      eventBus.emit({ type: "circuit:half_open", serviceId: this.config.serviceId });
      logger.info(`HttpCircuitBreaker[${this.config.serviceId}]: HALF_OPEN - probing`);

    } else if (newState === "closed") {
      this.failureCount = 0;
      this.openedAt     = null;
      eventBus.emit({
        type:           "circuit:closed",
        serviceId:      this.config.serviceId,
        probeSucceeded: previous === "half_open",
      });
      logger.info(`HttpCircuitBreaker[${this.config.serviceId}]: CLOSED - service recovered`);
    }
  }

  /* ── QUERY API ──────────────────────────────────────── */

  /**
   * Current health snapshot - suitable for health monitors and dashboards.
   * NEVER report the guarded service as "healthy" based on this alone -
   * only "closed" state indicates the circuit is not actively blocking calls.
   */
  get healthSnapshot(): CircuitHealthSnapshot {
    return {
      serviceId:       this.config.serviceId,
      state:           this.state,
      failureCount:    this.failureCount,
      lastFailedAt:    this.lastFailedAt,
      lastSucceededAt: this.lastSucceededAt,
      totalRejected:   this.totalRejected,
      openedAt:        this.openedAt,
    };
  }

  /**
   * Is the circuit currently passing requests (not actively blocking)?
   * "true" = closed or half-open (probe in progress)
   * "false" = open (requests are being rejected)
   */
  isPassingRequests(): boolean {
    if (this.state === "closed")   return true;
    if (this.state === "half_open") return true;
    // Open - check if reset window has passed (about to probe)
    const elapsed = Date.now() - (this.lastFailedAt ?? 0);
    return elapsed >= this.config.resetWindowMs;
  }

  get serviceId(): string { return this.config.serviceId; }
  get currentState(): CircuitState { return this.state; }
}

/* =========================================================
   SINGLETON CIRCUIT BREAKER INSTANCES

   These are the only authorized circuit breakers for their respective services.
   All calls to Supabase, Anthropic, and OpenAI must go through these instances.

   GOVERNANCE: Creating additional circuit breaker instances for the same service
   is forbidden - it splits the failure tracking state and produces inaccurate
   circuit behavior.
   ========================================================= */

/**
 * Protects all Supabase read/write operations.
 * Used by: SupabaseAdapter (storageAdapter.ts), acuPersistenceBridge.ts.
 */
export const supabaseCircuit = new HttpCircuitBreaker({
  serviceId:        "supabase",
  failureThreshold: 5,       // 5 consecutive failures to open
  resetWindowMs:    30_000,  // 30s before half-open probe
  timeoutMs:        15_000,  // 15s per-call timeout
  halfOpenMaxCalls: 1,       // 1 successful probe to close
  jitterFactor:     0.2,     // ±20% timeout jitter
});

/**
 * Protects all Anthropic (Claude) API calls.
 * Used by: anthropicProvider.ts, any AI pipeline calling Claude directly.
 */
export const anthropicCircuit = new HttpCircuitBreaker({
  serviceId:        "anthropic",
  failureThreshold: 3,        // AI calls are expensive - open faster
  resetWindowMs:    60_000,   // 60s before probing (AI may be throttled)
  timeoutMs:        300_000,  // 300s - 16384-token app generation takes 120-240s
  halfOpenMaxCalls: 1,
  jitterFactor:     0.02,     // ±2% jitter only - near-zero randomness for predictable limits
});

/**
 * Protects all OpenAI API calls.
 * Used by: openAIProvider.ts.
 */
export const openaiCircuit = new HttpCircuitBreaker({
  serviceId:        "openai",
  failureThreshold: 3,
  resetWindowMs:    60_000,
  timeoutMs:        30_000,
  halfOpenMaxCalls: 1,
  jitterFactor:     0.2,
});

/**
 * Protects all Mistral API calls (OpenAI-compatible endpoint).
 * Used by: openAICompatibleProvider.ts (mistralProvider).
 */
export const mistralCircuit = new HttpCircuitBreaker({
  serviceId:        "mistral",
  failureThreshold: 3,
  resetWindowMs:    60_000,
  timeoutMs:        120_000,
  halfOpenMaxCalls: 1,
  jitterFactor:     0.2,
});

/**
 * Protects all Groq API calls (OpenAI-compatible endpoint).
 * Used by: openAICompatibleProvider.ts (groqProvider).
 */
export const groqCircuit = new HttpCircuitBreaker({
  serviceId:        "groq",
  failureThreshold: 3,
  resetWindowMs:    60_000,
  timeoutMs:        60_000,
  halfOpenMaxCalls: 1,
  jitterFactor:     0.2,
});
